import { Link } from 'react-router-dom'
import { Box, Database, Globe, Code, Wrench, ShieldCheck, Clock } from 'lucide-react'
import type { Image } from '@/types'
import { cn, formatRelativeTime } from '@/lib/utils'
import CVEBadge from './CVEBadge'

interface ImageCardProps {
  image: Image
}

const categoryIcons = {
  base: Box,
  runtime: Code,
  web: Globe,
  database: Database,
  tool: Wrench,
}

const categoryColors = {
  base: 'text-blue-400',
  runtime: 'text-purple-400',
  web: 'text-orange-400',
  database: 'text-green-400',
  tool: 'text-yellow-400',
}

export default function ImageCard({ image }: ImageCardProps) {
  const Icon = categoryIcons[image.category] || Box

  return (
    <Link to={`/images/${encodeURIComponent(image.name)}`} className="block">
      <div className="card-hover p-5">
        <div className="flex items-start gap-4">
          <div className={cn(
            'p-3 rounded-lg bg-dark-800',
            categoryColors[image.category]
          )}>
            <Icon className="h-6 w-6" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-white truncate">{image.name}</h3>
              {image.verified && (
                <ShieldCheck className="h-4 w-4 text-accent-500 flex-shrink-0" />
              )}
            </div>

            <p className="text-sm text-dark-400 mb-3 line-clamp-2">
              {image.description}
            </p>

            <div className="flex items-center gap-3 text-xs">
              <CVEBadge count={image.cveCount} />

              <span className="badge-neutral capitalize">
                {image.category}
              </span>

              <span className="flex items-center gap-1 text-dark-500">
                <Clock className="h-3 w-3" />
                {formatRelativeTime(image.lastUpdated)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  )
}
